import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, EyeOff, Eye, Trash2, Send, ExternalLink, Image as ImageIcon, X, Heart } from 'lucide-react';
import { fetchComments, replyToComment, hideComment, deleteComment } from '../../redux/instagramSlice';
import alertService from '../../utils/alert';

function groupCommentsByPost(comments, media) {
  const groups = new Map();
  for (const c of comments) {
    if (!groups.has(c.mediaId)) {
      groups.set(c.mediaId, { mediaId: c.mediaId, info: media[c.mediaId] || {}, comments: [] });
    }
    groups.get(c.mediaId).comments.push(c);
  }
  // Comments within a group already arrive sorted newest-first from the API;
  // order the groups themselves by their most recent comment.
  return [...groups.values()].sort((a, b) => {
    const aLatest = new Date(a.comments[0]?.timestamp || 0).getTime();
    const bLatest = new Date(b.comments[0]?.timestamp || 0).getTime();
    return bLatest - aLatest;
  });
}

function CommentRow({ c, replyDraft, isPrivate, onDraftChange, onPrivateChange, onSubmit, onHide, onDelete }) {
  return (
    <div className={`p-4 ${c.hidden ? 'bg-[#FFF4E5]/40' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-[#2E2E2E]">
            {c.from?.username ? `@${c.from.username}` : 'Unknown user'}
            {c.isMention && <span className="ml-2 text-[11px] text-[#0176D3] font-bold">MENTION</span>}
            {c.hidden && <span className="ml-2 text-[11px] text-[#DD7A01] font-bold">HIDDEN</span>}
          </div>
          <div className="text-[13px] text-[#2E2E2E] mt-1">{c.text}</div>
          {c.replyText && (
            <div className="mt-2 text-[12px] text-[#5C5C5C] border-l-2 border-[#E5E5E5] pl-2">
              Replied: {c.replyText}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            title={c.hidden ? 'Unhide' : 'Hide'}
            onClick={onHide}
            className="p-1.5 rounded-[4px] border border-[#E5E5E5] hover:border-[#0176D3] text-[#5C5C5C] hover:text-[#0176D3]"
          >
            {c.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button
            title="Delete" aria-label="Delete"
            onClick={onDelete}
            className="p-1.5 rounded-[4px] border border-[#E5E5E5] hover:border-[#BA0517] text-[#5C5C5C] hover:text-[#BA0517]"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input
          value={replyDraft || ''}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          placeholder={isPrivate ? 'Send a private DM reply…' : 'Reply publicly to this comment…'}
          className="flex-1 border border-[#E5E5E5] rounded-[4px] px-3 py-1.5 text-[13px] focus:outline-none focus:border-[#0176D3]"
        />
        <label className="flex items-center gap-1 text-[11px] text-[#5C5C5C] whitespace-nowrap">
          <input type="checkbox" checked={!!isPrivate} onChange={(e) => onPrivateChange(e.target.checked)} />
          Private DM
        </label>
        <button
          onClick={onSubmit}
          className="flex items-center gap-1 bg-[#0176D3] hover:bg-[#0153A0] text-white text-[12px] font-semibold px-3 py-1.5 rounded-[4px] whitespace-nowrap"
        >
          <Send size={12} /> Send
        </button>
      </div>
    </div>
  );
}

function formatCount(n) {
  if (typeof n !== 'number') return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

function PostGridCell({ group, onOpen }) {
  const likeCount = formatCount(group.info.likeCount);
  return (
    <button
      onClick={onOpen}
      className="relative aspect-square overflow-hidden bg-[#E9ECF2] group focus:outline-none"
    >
      {group.info.thumbnailUrl ? (
        <img src={group.info.thumbnailUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#8E8E8E]">
          <ImageIcon size={28} />
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 py-2 bg-gradient-to-t from-black/70 to-transparent text-white">
        {likeCount !== null && (
          <span className="flex items-center gap-1" title="Instagram doesn't expose who liked a post via its API — count only">
            <Heart size={15} fill="white" />
            <span className="text-[13px] font-bold">{likeCount}</span>
          </span>
        )}
        <span className="flex items-center gap-1">
          <MessageCircle size={15} fill="white" />
          <span className="text-[13px] font-bold">{group.comments.length}</span>
        </span>
      </div>
      {group.comments.some((c) => c.hidden) && (
        <span className="absolute top-1.5 right-1.5 text-[10px] font-bold text-[#8C4B02] bg-[#FFB75D] rounded-full px-1.5 py-0.5">
          HIDDEN
        </span>
      )}
    </button>
  );
}

export default function InstagramCommentsPage() {
  const dispatch = useDispatch();
  const { comments, commentsMedia, loading, error } = useSelector((s) => s.instagram);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [asPrivate, setAsPrivate] = useState({});
  const [activeMediaId, setActiveMediaId] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    dispatch(fetchComments());
  }, [dispatch]);

  const groups = useMemo(() => groupCommentsByPost(comments, commentsMedia), [comments, commentsMedia]);
  const activeGroup = groups.find((g) => g.mediaId === activeMediaId) || null;

  // Deep-link from a notification — open that post's comment modal as soon as
  // its group has loaded (may not be present yet if it's outside the default
  // most-recent page of comments).
  useEffect(() => {
    const mediaId = searchParams.get('mediaId');
    if (mediaId && groups.some((g) => g.mediaId === mediaId)) {
      setActiveMediaId(mediaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, groups]);

  const submitReply = (commentId) => {
    const message = (replyDrafts[commentId] || '').trim();
    if (!message) return;
    dispatch(replyToComment({ commentId, message, asPrivateReply: !!asPrivate[commentId] }));
    setReplyDrafts((prev) => ({ ...prev, [commentId]: '' }));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <MessageCircle size={18} className="text-[#E1306C]" />
        <h2 className="text-[16px] font-bold text-[#2E2E2E]">Comment Moderation</h2>
      </div>

      {error && <div className="text-[13px] text-[#BA0517]">{error}</div>}
      {loading && comments.length === 0 && <div className="text-[13px] text-[#5C5C5C]">Loading comments…</div>}
      {!loading && comments.length === 0 && (
        <div className="text-[13px] text-[#5C5C5C] bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6 text-center">
          No comments yet. New comments on your Instagram posts will appear here automatically.
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-1">
        {groups.map((group) => (
          <PostGridCell key={group.mediaId} group={group} onOpen={() => setActiveMediaId(group.mediaId)} />
        ))}
      </div>

      {activeGroup && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActiveMediaId(null)} />
          <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg max-h-[100dvh] sm:max-h-[85vh] z-10 overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 px-4 sm:px-6 py-3 bg-white border-b border-[#E5E5E5] flex-shrink-0">
              {activeGroup.info.thumbnailUrl ? (
                <img src={activeGroup.info.thumbnailUrl} alt="" className="w-11 h-11 rounded-[4px] object-cover flex-shrink-0" />
              ) : (
                <div className="w-11 h-11 rounded-[4px] bg-[#E9ECF2] flex items-center justify-center flex-shrink-0 text-[#8E8E8E]">
                  <ImageIcon size={18} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-[#2E2E2E] truncate">
                  {activeGroup.info.caption || <span className="text-[#8E8E8E] italic">No caption</span>}
                </div>
                <div className="text-[11px] text-[#5C5C5C] flex items-center gap-2">
                  <span>{activeGroup.comments.length} comment{activeGroup.comments.length === 1 ? '' : 's'}</span>
                  {formatCount(activeGroup.info.likeCount) !== null && (
                    <span className="flex items-center gap-0.5" title="Instagram doesn't expose who liked a post via its API — count only">
                      <Heart size={11} /> {formatCount(activeGroup.info.likeCount)}
                    </span>
                  )}
                </div>
              </div>
              {activeGroup.info.permalink && (
                <a
                  href={activeGroup.info.permalink}
                  target="ig_view_post"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[11px] font-semibold text-[#0176D3] hover:underline flex-shrink-0"
                >
                  View post <ExternalLink size={12} />
                </a>
              )}
              <button onClick={() => setActiveMediaId(null)} className="text-[#5C5C5C] hover:text-[#2E2E2E] flex-shrink-0">
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto divide-y divide-[#E5E5E5]">
              {activeGroup.comments.map((c) => (
                <CommentRow
                  key={c.commentId}
                  c={c}
                  replyDraft={replyDrafts[c.commentId]}
                  isPrivate={asPrivate[c.commentId]}
                  onDraftChange={(v) => setReplyDrafts((prev) => ({ ...prev, [c.commentId]: v }))}
                  onPrivateChange={(v) => setAsPrivate((prev) => ({ ...prev, [c.commentId]: v }))}
                  onSubmit={() => submitReply(c.commentId)}
                  onHide={() => dispatch(hideComment({ commentId: c.commentId, hide: !c.hidden }))}
                  onDelete={async () => (await alertService.confirm('Delete?', 'Delete this comment?', 'danger', 'Delete')) && dispatch(deleteComment(c.commentId))}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
